/**
 * A semaphore implementation for controlling concurrent access to a shared resource.
 * Limits the number of concurrent operations to a specified maximum.
 */
export default class Semaphore {
  /** Current number of active operations */
  private count: number

  /** Queue of pending operations waiting to acquire the semaphore */
  private queue: (() => void)[]

  /**
   * Creates a new semaphore with the specified concurrency limit
   * @param limit Maximum number of concurrent operations allowed
   * @throws Error if limit is less than 1
   */
  constructor(private limit: number) {
    if (limit < 1) {
      throw new Error('Semaphore limit must be at least 1')
    }
    this.count = 0
    this.queue = []
  }

  /**
   * Acquires the semaphore, allowing execution to proceed if under the limit
   * or queuing the operation if at the limit
   * @returns Promise that resolves when the semaphore is acquired
   * @throws Error if semaphore is in an invalid state
   */
  async acquire(): Promise<void> {
    if (this.count < 0) {
      throw new Error('Semaphore count cannot be negative')
    }
    if (this.count < this.limit) {
      this.count += 1
      return Promise.resolve()
    }
    return new Promise<void>((resolve) => {
      this.queue.push(resolve)
    })
  }

  /**
   * Releases the semaphore, allowing the next queued operation to proceed
   * @throws Error if semaphore is in an invalid state
   */
  async release(): Promise<void> {
    if (this.count <= 0) {
      throw new Error('Cannot release semaphore when count is 0 or negative')
    }
    this.count -= 1
    if (this.queue.length > 0) {
      const resolve = this.queue.shift()
      if (resolve) {
        resolve()
      }
    }
  }

  /**
   * Executes a function with semaphore control
   * @param fn Function to execute
   * @throws Error if the function execution fails
   */
  async execute(fn: () => Promise<void>): Promise<void> {
    if (!fn) {
      throw new Error('Function to execute cannot be null or undefined')
    }
    await this.acquire()
    try {
      await fn()
    } finally {
      await this.release()
    }
  }
}
